import {
  Button,
  Icon,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  TableColumn,
} from '@components';
import { useModal } from '@hooks';
import { useConfirmationModal } from '@hooks/useConfirmationModal';
import CostCenterAPICaller, {
  CostCenterFilters,
} from '@services/api/costCenter';
import UsersAPICaller from '@services/api/user';
import { departmentEnum } from 'enums';
import { CostCenter } from 'interfaces/CostCenter';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { CostCenterUsersTable } from './CostCenterUsersTable';

export default function CostCenterPage() {
  const { isOpen, toggle: toggleCostCenterModal } = useModal();
  const confirmation = useConfirmationModal();
  const {
    watch,
    register,
    setError,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [tableData, setTableData] = useState<{
    total: number;
    result: Array<CostCenter>;
  }>({
    total: 0,
    result: [],
  });

  const [expandedId, setExpandedId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<CostCenterFilters>({});
  const [users, setUsers] = useState([]);
  const { createOrUpdate, fetch, remove } = CostCenterAPICaller;

  useEffect(() => {
    loadTableData(filters);
    loadUsers();
  }, [filters]);

  const loadUsers = () => {
    UsersAPICaller.listAll().then((values) => {
      const { result: users } = values;
      setUsers(users);
    });
  };

  const loadTableData = (filters: CostCenterFilters) => {
    setIsLoading(true);
    CostCenterAPICaller.list(filters)
      .then((values) => setTableData(values))
      .finally(() => setIsLoading(false));
  };

  const openEditModal = async (editId: string) => {
    const costCenter = await fetch(editId);
    setValue('id', costCenter.id);
    setValue('name', costCenter.name);
    setValue('department', {
      label: costCenter.department,
      value: costCenter.department,
    });
    toggleCostCenterModal();
  };

  const handleRemoveCostCenter = async (removeId: string) => {
    confirmation({
      title: 'Excluir centro de custo',
      confirmButtonTitle: 'Excluir',
      description: 'Deseja realmente excluir esse centro de custo?',
      onSubmit: () =>
        remove(removeId)
          .then((_) => loadTableData(filters))
          .catch((error) => {
            const message = error.response.data.message || 'Erro ao excluir!';
            toast.error(message);
          }),
    });
  };

  const handleCreateOrUpdate = async (data: CostCenter) => {
    createOrUpdate(data, setError).then((res) => {
      if (res.data.errors) return;
      toggleCostCenterModal();
      loadTableData(filters);
    });
  };

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Centro de custo" />

        <div className="mt-s-200">
          <Table
            dataKey="id"
            data={tableData.result}
            showColumnSelector={false}
            expandedRow={expandedId}
            totalRecords={tableData.total}
            withPagination
            withSearchInput
            filters={filters}
            isLoading={isLoading}
            setFilters={setFilters}
            additionalFilters={
              <div className="d-flex justify-end">
                <Button
                  className="ml-s-100"
                  suffixes={<Icon>add</Icon>}
                  design="filled"
                  onClick={() => {
                    setValue('id', undefined);
                    setValue('name', '');
                    setValue('department', undefined);
                    return toggleCostCenterModal();
                  }}
                >
                  Novo centro de custo
                </Button>
              </div>
            }
            renderExpandedRow={(costCenter) => (
              <CostCenterUsersTable
                key={costCenter.id}
                costCenterName={costCenter.name}
                costCenterId={costCenter.id}
                users={users}
                onUpdate={() => loadTableData(filters)}
              />
            )}
          >
            <TableColumn fromKey="name" header="Centro de custo" />
            <TableColumn fromKey="department" header="Departamento" />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Ações"
              disableSorting
              render={(id) => {
                return (
                  <div className="d-flex">
                    <Button
                      design="transparent"
                      prefixes={<Icon>edit</Icon>}
                      onClick={() => {
                        openEditModal(id);
                      }}
                    />
                    <Button
                      design="transparent"
                      prefixes={<Icon>delete</Icon>}
                      onClick={async () => handleRemoveCostCenter(id)}
                    />
                    <Button
                      weight="bold"
                      design="transparent"
                      prefixes={
                        <Icon>
                          {expandedId === id ? 'expand_less' : 'expand_more'}
                        </Icon>
                      }
                      onClick={() => {
                        if (expandedId === id) return setExpandedId('');
                        else setExpandedId(id);
                      }}
                    />
                  </div>
                );
              }}
            />
          </Table>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onClickAway={toggleCostCenterModal}
        size="extra-small"
        title="Cadastrar centro de custo"
      >
        <form
          onSubmit={handleSubmit((data) =>
            handleCreateOrUpdate(data as CostCenter)
          )}
        >
          <div className="mb-s-200">
            <Input
              label="Centro de custo"
              error={!!errors.name}
              caption={errors.name?.message as string}
              placeholder="Digite um centro de custo"
              form={register('name', { required: 'Obrigatório' })}
            />
          </div>
          <div className="mb-s-200">
            <Select
              label="Departamento"
              value={watch('department')}
              error={!!errors.department}
              caption={errors.department?.message as string}
              placeholder="Selecione um departamento"
              form={register('department', { required: 'Obrigatório' })}
              onSelect={(value: string | undefined) => {
                setValue('department', value);
              }}
              options={departmentEnum}
            />
          </div>
          <Button className="!w-100" type="submit" alignText="center">
            Salvar
          </Button>
        </form>
      </Modal>
    </>
  );
}
